import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {ActionBase, ActionWithPayload} from '../../../helpers/reduxHelpers';
import {ApiIntegrationTypes, IntegrationTypeModel} from '../../../models/api/integrationTypesModel';
import {fetchApiAsync, fetchAsync} from '../../../services/http/http';
import {HttpError} from '../../../services/http/httpError';
import {ThunkAsyncAction} from '../../appThunk';
import {getCompanyUrl} from '../company/companySelectors';
import {getIntegrationTypes, getShouldFetchIntegrationTypes} from './integrationTypesSelectors';

/*
 * Action types.
 */

export enum IntegrationTypesActionsEnum {
  FETCH_INTEGRATION_TYPES = '@@data/integrationTypes/FETCH_INTEGRATION_TYPES',
  FETCH_INTEGRATION_TYPES_COMPLETED = '@@data/integrationTypes/FETCH_INTEGRATION_TYPES_COMPLETED',
  FETCH_INTEGRATION_TYPES_FAILED = '@@data/integrationTypes/FETCH_INTEGRATION_TYPES_FAILED',
  FETCH_INTEGRATION_TYPE_DESCRIPTION_COMPLETED = '@@data/integrationTypes/FETCH_INTEGRATION_TYPE_DESCRIPTION_COMPLETED',
}

/*
 * Types.
 */

export interface FetchIntegrationTypesAction extends ActionBase {
  type: IntegrationTypesActionsEnum.FETCH_INTEGRATION_TYPES;
}

export interface FetchIntegrationTypesCompletedAction
  extends ActionWithPayload<ReadonlyArray<IntegrationTypeModel>> {
  type: IntegrationTypesActionsEnum.FETCH_INTEGRATION_TYPES_COMPLETED;
}

export interface FetchIntegrationTypesFailedAction extends ActionBase {
  type: IntegrationTypesActionsEnum.FETCH_INTEGRATION_TYPES_FAILED;
}

interface FetchIntegrationTypeDescriptionCompletedActionPayload {
  integrationId: string;
  description: string;
}

export interface FetchIntegrationTypeDescriptionCompletedAction
  extends ActionWithPayload<FetchIntegrationTypeDescriptionCompletedActionPayload> {
  type: IntegrationTypesActionsEnum.FETCH_INTEGRATION_TYPE_DESCRIPTION_COMPLETED;
}

/*
 * Action creators.
 */

function fetchIntegrationTypesAction(): FetchIntegrationTypesAction {
  return {
    type: IntegrationTypesActionsEnum.FETCH_INTEGRATION_TYPES,
  };
}

function fetchIntegrationTypesActionCompleted(
  integrationTypes: ReadonlyArray<IntegrationTypeModel>,
): FetchIntegrationTypesCompletedAction {
  return {
    type: IntegrationTypesActionsEnum.FETCH_INTEGRATION_TYPES_COMPLETED,
    payload: integrationTypes,
  };
}

function fetchIntegrationTypesActionFailed(): FetchIntegrationTypesFailedAction {
  return {
    type: IntegrationTypesActionsEnum.FETCH_INTEGRATION_TYPES_FAILED,
  };
}

function fetchIntegrationTypeDescriptionCompletedAction(
  integrationId: string,
  description: string,
): FetchIntegrationTypeDescriptionCompletedAction {
  return {
    type: IntegrationTypesActionsEnum.FETCH_INTEGRATION_TYPE_DESCRIPTION_COMPLETED,
    payload: {
      integrationId,
      description,
    },
  };
}

/*
 * Actions.
 */

export function fetchIntegrationTypesAsyncAction(): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // Check if we should fetch.
    const shouldFetchIntegrationTypes = getShouldFetchIntegrationTypes(getState());
    if (!shouldFetchIntegrationTypes) {
      return;
    }

    // Dispatch that we are loading integration types.
    dispatch(fetchIntegrationTypesAction());

    try {
      const companyUrl = getCompanyUrl(getState());
      const integrationTypes = await fetchApiAsync(ApiIntegrationTypes, `${companyUrl}/integration_types`);
      dispatch(fetchIntegrationTypesActionCompleted(integrationTypes));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      dispatch(fetchIntegrationTypesActionFailed());
    }
  };
}

export function maybeFetchIntegrationTypeDescriptionAsyncAction(integrationId: string): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // Check if we should fetch.
    const integrationTypes = getIntegrationTypes(getState());
    const integrationType = integrationTypes[integrationId];

    // If we already have the description we do not need to refetch it.
    if (integrationType?.longDescription) {
      return;
    }

    try {
      const companyUrl = getCompanyUrl(getState());
      const description: string = await fetchAsync(
        `${companyUrl}/integration_types/${integrationId}/public/docs/description.md`,
      );
      dispatch(fetchIntegrationTypeDescriptionCompletedAction(integrationId, description));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      // TODO: show an error state.
    }
  };
}

/*
 * Union type.
 */

export type IntegrationTypesAction =
  | FetchIntegrationTypesAction
  | FetchIntegrationTypesCompletedAction
  | FetchIntegrationTypesFailedAction
  | FetchIntegrationTypeDescriptionCompletedAction;
